import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTheme } from 'hooks/useTheme';
import { Global } from '@emotion/core';
import { useLocale } from 'locale';
import IconSearch from 'assets/svg/investors-header-search.svg';
import HouseIcon from 'assets/svg/investors-header-house-line.svg';
import ClearIcon from 'assets/svg/investors-header-clear.svg';
import { CompletionType, IAutocompleteEntry, IAutocompleteNeighbourhood } from 'utils/entities';
import AutocompleteInput from 'components/autocomplete/single-location-autocomplete';
import { navigateTo } from 'store/state/router/actions';
import { NavigateToFn, Route } from 'config/routes';
import { investorFavoriteNeighbourhoodsSelector, investorRecentSearchesSelector } from 'store/state/domainData/selectors/investors';
import { IFavoriteNeighbourhood } from 'store/sagas/apiService/types';
import { State } from 'store/state';
import { setInvestorsRecentSearchIdAction } from 'store/state/app/actions';
import {
  AutocompleteWrapper, ClearButton, DoneStateWrapper, mobileSuggestionsGlobalStyles, SearchIcon,
  SuggestionIconBox, ValueDivider, ValueGroup, ValueLabel, Wrapper,
} from './HeaderSearch.styled';

interface HeaderSearchProps {
  navigate: NavigateToFn;
  favoriteNeighbourhoods: IFavoriteNeighbourhood[];
  saveSearch: typeof setInvestorsRecentSearchIdAction;
  searchHistory: Array<{
    doc: {
      docId: string;
      ref?: IAutocompleteNeighbourhood;
    };
  }>;
  // opens the autocomplete immediately on mount (mobile: the full-screen search overlay). Used by
  // the mobile header so a single tap on the search icon lands straight in the search, instead of
  // showing an inert field that needs a second tap.
  autoFocus?: boolean;
  // called when the autocomplete closes (mobile overlay's cancel / blur). Lets the mobile header
  // collapse straight back to the search icon instead of leaving the inline field behind.
  onClose?: () => void;
}

// favoriteNeighbourhoods.gql / investorRecentSearches.gql both fetch `name` (pre-combined
// "{neighbourhood}, {city}"), plus the separate `neighbourhood`/`city` fields — confirmed via the
// actual .gql query files, since IAutocompleteNeighbourhood's declared `borough` field (used by
// the general-purpose autocomplete API) doesn't exist on this investor-specific shape at runtime.
interface NeighbourhoodRefFields {
  neighbourhood?: string;
  city?: string;
}

const HeaderSearchBase: React.FC<HeaderSearchProps> = ({ navigate, favoriteNeighbourhoods, saveSearch, searchHistory, autoFocus, onClose }) => {
  const { t } = useLocale();
  const theme = useTheme();
  const [ searchStr, setSearchStr ] = useState('');
  // "done" state (Figma search_box_done): once a neighbourhood is picked, collapse to a compact
  // pill showing just its name + a clear button, instead of the live-editable input. Only a real
  // selection moves editing -> done (not blur), and only clicking the pill moves done -> editing
  // again — deliberately avoids any blur-vs-click-on-suggestion race condition.
  const [ selectedLabel, setSelectedLabel ] = useState<string | null>(null);
  const [ isEditing, setIsEditing ] = useState(false);

  const onAutocompleteInputChange = (value: IAutocompleteEntry) => {
    if (value && value.type.toLowerCase() === CompletionType.Neighbourhood) {
      navigate(Route.InvestorsNeighbourhoodPage, { id: value.identityDocId });
      saveSearch(value.docId);
      setSelectedLabel(value.name);
      setIsEditing(false);
    }
  };

  const handleStartEditing = () => {
    setSearchStr('');
    setIsEditing(true);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLabel(null);
    setSearchStr('');
    setIsEditing(true);
  };

  // show the plain hood name as the row's bold label (matching Figma's Default/Variant2 rows),
  // not the backend's pre-combined "{neighbourhood}, {city}" `name` field
  const toDisplayEntry = (ref: IAutocompleteNeighbourhood & NeighbourhoodRefFields) => ({
    ...ref,
    name: ref.neighbourhood || ref.name,
  });

  // `doc.ref` is optional in the type — filter out any entry missing it so toDisplayEntry never
  // dereferences undefined (a single malformed favorite/recent-search would otherwise throw and take
  // down the whole search dropdown for that render).
  const favoriteList = (favoriteNeighbourhoods || [])
    .filter(el => el.doc && el.doc.ref)
    .map(el => toDisplayEntry(el.doc.ref as IAutocompleteNeighbourhood & NeighbourhoodRefFields));
  const historyList = (searchHistory || [])
    .filter(el => el.doc && el.doc.ref)
    .map(el => toDisplayEntry(el.doc.ref as IAutocompleteNeighbourhood & NeighbourhoodRefFields));

  if (selectedLabel && !isEditing) {
    return (
      <DoneStateWrapper data-auto="investors_home_v2_header_search" onClick={handleStartEditing}>
        <ValueGroup>
          <SearchIcon aria-hidden><IconSearch /></SearchIcon>
          <ValueDivider />
          <ValueLabel>{selectedLabel}</ValueLabel>
        </ValueGroup>
        <ClearButton data-auto="investors_home_v2_header_search_clear" aria-label={t('investors.home.v2.header.search.clear')} onClick={handleClear}>
          <ClearIcon aria-hidden />
        </ClearButton>
      </DoneStateWrapper>
    );
  }

  return (
    <Wrapper data-auto="investors_home_v2_header_search">
      {/* on mobile, the shared autocomplete portals its input+dropdown into #sticky-root once
          opened — outside this component's own DOM tree, where our scoped CSS below can't reach
          it. See the comment on mobileSuggestionsGlobalStyles for the full explanation. */}
      <Global styles={mobileSuggestionsGlobalStyles(theme)} />
      <SearchIcon aria-hidden>
        <IconSearch />
      </SearchIcon>
      <AutocompleteWrapper>
        <AutocompleteInput
          searchStr={searchStr}
          placeholder={t('investors.autocomplete.placeholder')}
          inputIcon={null}
          completionTypes={[ CompletionType.Neighbourhood ]}
          optionsCount={6}
          onSearchStrChange={setSearchStr}
          onChange={onAutocompleteInputChange}
          autoFocus={autoFocus || isEditing}
          onBlur={onClose}
          showClearButton
          suggestionItemIcon={<SuggestionIconBox aria-hidden><HouseIcon width={24} height={24} /></SuggestionIconBox>}
          // show the bare hood name as the bold row label — never the backend's pre-combined
          // "{neighbourhood}, {city}" `name`. Returning a plain string also opts these rows out of the
          // shared typed-match highlight span, so no turquoise letters appear in this section. The city
          // is surfaced separately below the name via formatSecondaryLabel + the dropdown's grid layout.
          formatPrimaryLabel={(entry) => {
            const ref = entry as IAutocompleteEntry & NeighbourhoodRefFields;
            return ref.neighbourhood || ref.name;
          }}
          formatSecondaryLabel={(entry) => {
            // `city` covers the favorites/recent shape (see NeighbourhoodRefFields above);
            // `borough` covers IAutocompleteNeighbourhood's own field for live-typed search results
            const ref = entry as IAutocompleteEntry & NeighbourhoodRefFields & { borough?: string };
            const city = ref.city || ref.borough;
            return city ? t('investors.home.v2.header.search.neighbourhoodInCity', { city }) : null;
          }}
          initialSuggestions={(favoriteList && favoriteList.length) || (historyList && historyList.length) ? [
            favoriteList && favoriteList.length ? {
              icon: null,
              title: t('investors.favorite.neighbourhoods.title.only'),
              entries: favoriteList as unknown as IAutocompleteEntry[],
            } : null,
            historyList && historyList.length ? {
              icon: null,
              title: t('investors.recentSearch.neighbourhoods.title.only'),
              entries: historyList,
            } : null,
          ].filter(Boolean) : null}
          suggestionsWithTitle
        />
      </AutocompleteWrapper>
    </Wrapper>
  );
};

const mapStateToProps = (state: State) => ({
  favoriteNeighbourhoods: investorFavoriteNeighbourhoodsSelector(state),
  searchHistory: investorRecentSearchesSelector(state),
});

const mapDispatchToProps = {
  navigate: navigateTo,
  saveSearch: setInvestorsRecentSearchIdAction,
};

export const HeaderSearch = connect(mapStateToProps, mapDispatchToProps)(HeaderSearchBase);
